import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  NewPageWrapper,
  NewPageWrapperCopy,
} from "../../../../ui-components/custom-components";
import { useUser } from "../../../../contexts/user-context";
import { useEthosNotification } from "../../../../contexts/ethos-notification-context";

type ExportSingleUserModalProps = {
  userId: string;
  isAdmin: boolean;
  closeModal: (value: boolean) => void;
  onDone?: () => void;
};

const filterOptions: { label: string; value: string }[] = [
  { label: "Pagati", value: "PAID" },
  { label: "Non Pagati", value: "NOT_PAID" },
  { label: "Tutti", value: "ALL" },
];

const ExportSingleUserModal = ({
  userId,
  isAdmin,
  closeModal,
  onDone,
}: ExportSingleUserModalProps) => {
  const { generateUsersReport, getTemplateUsers } = useUser();
  const { handleError, showErrorToast } = useEthosNotification();

  const [isExporting, setIsExporting] = useState(false);
  const [templatesOptions, setTemplatesOptions] = useState<
    { id: string; label: string; timestamp: number }[]
  >([]);

  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");

  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const hasInvalidRange = useMemo(() => {
    return Boolean(dateFrom && dateTo && dateFrom > dateTo);
  }, [dateFrom, dateTo]);

  const loadTemplates = useCallback(async () => {
    const { data: templatesData, error } = await getTemplateUsers();

    if (error) {
      handleError(error);
      return;
    }
    const toArray = [templatesData];

    const services = Array.isArray(toArray)
      ? toArray
      : (templatesData?.data ?? []);

    const activeTemplates = services
      .flatMap((service) => service?.templates ?? [])
      .filter((template) => template?.status === "ACTIVE" && template?.id)
      .sort((a, b) => {
        const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : -1;
        const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : -1;
        return bTime - aTime;
      });

    const options = activeTemplates.map((template) => {
      const name = template?.filename || "Template Compensi Utente";
      const dateValue = template?.createdAt;
      const timestamp = dateValue ? new Date(dateValue).getTime() : -1;
      const dateLabel =
        timestamp > 0
          ? new Date(dateValue).toLocaleDateString("it-IT")
          : "N.D.";
      return {
        id: template.id,
        label: `${name} - ${dateLabel}`,
        timestamp,
      };
    });
    setTemplatesOptions(options);
  }, [getTemplateUsers, handleError]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const downloadReportFile = (data: any) => {
    const url = data?.url;
    const filename = data?.filename || "report";
    if (!url) {
      showErrorToast("URL non disponibile per il download.");
      return;
    }

    const element = document.createElement("a");
    element.href = url;
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    element.remove();
  };

  const exportUser = async () => {
    try {
      setIsExporting(true);

      if (!selectedTemplateId) {
        showErrorToast("Seleziona un template per esportare.");
        return;
      }

      if (!selectedFilter) {
        showErrorToast("Seleziona un filtro per esportare.");
        return;
      }

      const body: {
        users: string[];
        startDate?: Date;
        endDate?: Date;
        paymentFilter?: string;
      } = { users: [userId] };

      if (dateFrom) body.startDate = new Date(dateFrom);
      if (dateTo) body.endDate = new Date(dateTo);
      if (selectedFilter) body.paymentFilter = selectedFilter;

      const { data, error } = await generateUsersReport(
        selectedTemplateId,
        body,
      );
      if (error) {
        handleError(error);
        return;
      }

      downloadReportFile(data);

      onDone?.();
      closeModal(false);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <NewPageWrapper>
      <NewPageWrapperCopy>
        <div className="top-page">
          <h3 className="top-page-title">Esporta dati utente</h3>

          <div className="d-flex gap-2">
            <button
              className="btn btn-secondary"
              disabled={isExporting}
              onClick={() => closeModal(false)}
            >
              Chiudi
            </button>

            <button
              className="btn btn-success"
              disabled={
                !isAdmin ||
                isExporting ||
                hasInvalidRange ||
                !selectedTemplateId
              }
              onClick={exportUser}
              title={hasInvalidRange ? "Intervallo date non valido" : undefined}
            >
              {isExporting ? "Esportazione..." : "Esporta"}
            </button>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-end my-3 gap-3 flex-wrap">
          <div className="d-flex align-items-end gap-2 flex-wrap">
            <div>
              <label className="form-label mb-1 small">Dal</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={isExporting}
              />
            </div>

            <div>
              <label className="form-label mb-1 small">Al</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={isExporting}
              />
            </div>

            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              disabled={isExporting || (!dateFrom && !dateTo)}
              title="Cancella filtro date"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>

            <select
              className="form-select form-select-sm"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              disabled={isExporting}
            >
              <option value="">Seleziona template</option>
              {templatesOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              disabled={isExporting}
            >
              <option value="">Seleziona un filto</option>
              {filterOptions.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            {hasInvalidRange && (
              <div className="text-danger small mb-1">
                Intervallo non valido (Dal &gt; Al)
              </div>
            )}
          </div>
        </div>

        <div className="small text-muted">
          Se vuoi, imposta un intervallo date per filtrare l’esportazione.
          Altrimenti esporta tutto.
        </div>
      </NewPageWrapperCopy>
    </NewPageWrapper>
  );
};

export default ExportSingleUserModal;
